import {
  PLAN_CHANGE_EVENT,
  STORAGE_KEYS,
  UDV_CHROME_THEMES,
  UDV_PLANS,
} from './constants'
import { parseToolbarQueryParam } from '../config/pacsConfig'
import { shellShortFromLayout } from './parsePlanQuery'
import { parseThemeQueryParam } from './chromeTheme'

function canUseStorage() {
  try {
    return typeof window !== 'undefined' && !!window.localStorage
  } catch (e) {
    return false
  }
}

function readJson(key, fallback) {
  if (!canUseStorage()) return fallback
  try {
    const raw = window.localStorage.getItem(key)
    if (raw == null || raw === '') return fallback
    return JSON.parse(raw)
  } catch (e) {
    return fallback
  }
}

function writeJson(key, value) {
  if (!canUseStorage()) return
  try {
    if (value == null) {
      window.localStorage.removeItem(key)
    } else {
      window.localStorage.setItem(key, JSON.stringify(value))
    }
  } catch (e) {
    /* ignore quota */
  }
}

export function notifyPlanChange() {
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(PLAN_CHANGE_EVENT))
    }
  } catch (e) {
    /* ignore */
  }
}

/**
 * @param {(ev?: Event) => void} handler
 * @returns {() => void}
 */
export function subscribePlanChange(handler) {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(PLAN_CHANGE_EVENT, handler)
  window.addEventListener('storage', handler)
  return () => {
    window.removeEventListener(PLAN_CHANGE_EVENT, handler)
    window.removeEventListener('storage', handler)
  }
}

/** @returns {Record<string, object>|null} */
export function getCatalogOverrides() {
  const v = readJson(STORAGE_KEYS.CATALOG_OVERRIDES, null)
  return v && typeof v === 'object' ? v : null
}

/** @param {Record<string, object>|null} map */
export function setCatalogOverrides(map) {
  writeJson(STORAGE_KEYS.CATALOG_OVERRIDES, map)
  notifyPlanChange()
}

/** @returns {object[]|null} */
export function getTokenOverrides() {
  const v = readJson(STORAGE_KEYS.TOKEN_OVERRIDES, null)
  return Array.isArray(v) ? v : null
}

/** @param {object[]|null} list */
export function setTokenOverrides(list) {
  writeJson(STORAGE_KEYS.TOKEN_OVERRIDES, list)
  notifyPlanChange()
}

/** @returns {string|null} */
export function getSimulatedPlan() {
  if (!canUseStorage()) return null
  try {
    const v = window.localStorage.getItem(STORAGE_KEYS.SIMULATE_PLAN)
    if (!v) return null
    const lower = String(v).toLowerCase()
    if (
      lower === UDV_PLANS.PATIENT ||
      lower === UDV_PLANS.DEMO ||
      lower === UDV_PLANS.LITE ||
      lower === UDV_PLANS.PRO ||
      lower === UDV_PLANS.PREMIUM ||
      lower === UDV_PLANS.SUPREMO
    ) {
      return lower
    }
    return null
  } catch (e) {
    return null
  }
}

/** @param {string|null} plan */
export function setSimulatedPlan(plan) {
  if (!canUseStorage()) return
  try {
    if (!plan) {
      window.localStorage.removeItem(STORAGE_KEYS.SIMULATE_PLAN)
    } else {
      window.localStorage.setItem(STORAGE_KEYS.SIMULATE_PLAN, String(plan))
    }
  } catch (e) {
    /* ignore */
  }
  notifyPlanChange()
}

/**
 * Lab: shell simulado (classic|edge|studio|ohif|oviyamng).
 * @returns {string|null} TOOLBAR_LAYOUTS value o null
 */
export function getSimulatedShell() {
  if (!canUseStorage()) return null
  try {
    const v = window.localStorage.getItem(STORAGE_KEYS.SIMULATE_SHELL)
    if (!v) return null
    return parseToolbarQueryParam(v)
  } catch (e) {
    return null
  }
}

/**
 * @param {string|null} shell — corto o TOOLBAR_LAYOUTS
 */
export function setSimulatedShell(shell) {
  if (!canUseStorage()) return
  try {
    if (!shell) {
      window.localStorage.removeItem(STORAGE_KEYS.SIMULATE_SHELL)
    } else {
      const layout = parseToolbarQueryParam(shell)
      const short = shellShortFromLayout(layout) || String(shell).toLowerCase()
      window.localStorage.setItem(STORAGE_KEYS.SIMULATE_SHELL, short)
    }
  } catch (e) {
    /* ignore */
  }
  notifyPlanChange()
}

/**
 * Lab: theme chrome (dark|brand|light).
 * @returns {'dark'|'brand'|'light'|null}
 */
export function getSimulatedTheme() {
  if (!canUseStorage()) return null
  try {
    const v = window.localStorage.getItem(STORAGE_KEYS.SIMULATE_THEME)
    return parseThemeQueryParam(v)
  } catch (e) {
    return null
  }
}

/**
 * @param {string|null} theme
 */
export function setSimulatedTheme(theme) {
  if (!canUseStorage()) return
  try {
    const parsed = parseThemeQueryParam(theme)
    if (!parsed || parsed === UDV_CHROME_THEMES.DARK) {
      // dark = default: limpiar override
      window.localStorage.removeItem(STORAGE_KEYS.SIMULATE_THEME)
    } else {
      window.localStorage.setItem(STORAGE_KEYS.SIMULATE_THEME, parsed)
    }
  } catch (e) {
    /* ignore */
  }
  notifyPlanChange()
}

/** @returns {boolean} */
export function getShowDraft() {
  if (!canUseStorage()) return true
  try {
    const v = window.localStorage.getItem(STORAGE_KEYS.SHOW_DRAFT)
    if (v == null || v === '') return true
    return v !== '0' && v !== 'false' && v !== 'off'
  } catch (e) {
    return true
  }
}

/** @param {boolean} on */
export function setShowDraft(on) {
  if (!canUseStorage()) return
  try {
    window.localStorage.setItem(STORAGE_KEYS.SHOW_DRAFT, on ? 'true' : 'false')
  } catch (e) {
    /* ignore */
  }
  notifyPlanChange()
}

export function resetPlanLabOverrides() {
  setCatalogOverrides(null)
  setTokenOverrides(null)
  setSimulatedPlan(null)
  setSimulatedShell(null)
  setSimulatedTheme(null)
  setShowDraft(true)
  setPortalSim(false)
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(STORAGE_KEYS.PORTAL_CHROME)
    }
  } catch (e) {
    /* ignore */
  }
  notifyPlanChange()
}

/** @returns {boolean} */
export function getPortalSim() {
  if (!canUseStorage()) return false
  try {
    const v = window.localStorage.getItem(STORAGE_KEYS.PORTAL_SIM)
    return v === '1' || v === 'true' || v === 'on'
  } catch (e) {
    return false
  }
}

/**
 * Simular perfil portal (patient) en developer.
 * @param {boolean} on
 */
export function setPortalSim(on) {
  if (!canUseStorage()) return
  try {
    if (on) {
      window.localStorage.setItem(STORAGE_KEYS.PORTAL_SIM, 'true')
    } else {
      window.localStorage.removeItem(STORAGE_KEYS.PORTAL_SIM)
    }
  } catch (e) {
    /* ignore */
  }
  notifyPlanChange()
}
